import { useEffect } from 'react';

import ROUTES from '../config/routes';

export const useIntersectionObserver = (refs, setActiveSection) => {
  useEffect(() => {
    if (!refs || !setActiveSection) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.6 }
    );

    Object.values(refs).forEach(ref => {
      if (ref && ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [refs, setActiveSection]);
};

export const isMainRouteCheck = path => {
  const mainRoutes = [
    ROUTES.HOME,
    ROUTES.OCTOPUSSYNC,
    ROUTES.SENTINEL,
    ROUTES.LOGIN,
    ROUTES.CONTACTS,
  ];
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  return mainRoutes.includes(normalizedPath);
};

export const navigateToPathOrSection = (
  id,
  path,
  refs,
  navigate,
  currentPath,
  startPageTransition
) => {
  const targetPath = id ? `${path}#${id}` : path;
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  const normalizedCurrentPath =
    currentPath !== '/' && currentPath.endsWith('/')
      ? currentPath.slice(0, -1)
      : currentPath;

  if (normalizedPath !== normalizedCurrentPath) {
    if (startPageTransition && isMainRouteCheck(normalizedPath)) {
      startPageTransition(() => {
        navigate(targetPath);
      });
    } else {
      navigate(targetPath);
    }
  } else {
    if (id) {
      window.__navScrollInProgress = true;

      const element = refs && refs[id]?.current;
      if (element) {
        const offset = 100;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        if (window.location.hash !== `#${id}`) {
          navigate(targetPath, { replace: true });
        }

        setTimeout(() => {
          window.__navScrollInProgress = false;
        }, 200);
      } else {
        navigate(targetPath, { replace: true });
        setTimeout(() => {
          window.__navScrollInProgress = false;
        }, 200);
      }
    }
  }
};

export const getLogo = (textColor) => {
  // Return the appropriate horizontal logo based on text color
  if (textColor === '#ffffff' || textColor === 'white') {
    return '/logos/octopus-horizontal-white.svg';
  }
  return '/logos/octopus-horizontal-purple.svg';
};

export const getNavigationMenuItems = () => {
  return [
    {
      label: 'Home',
      path: ROUTES.HOME,
      hasDropdown: false
    },
    {
      label: 'OctopusSync',
      path: ROUTES.OCTOPUSSYNC,
      hasDropdown: true,
      submenu: [
        {
          label: 'About',
          path: `${ROUTES.OCTOPUSSYNC}#about`,
          id: 'about'
        },
        {
          label: 'Features',
          path: `${ROUTES.OCTOPUSSYNC}#features`,
          id: 'features'
        },
        {
          label: 'Comparison',
          path: `${ROUTES.OCTOPUSSYNC}#comparison`,
          id: 'comparison'
        },
        {
          label: 'Pricing',
          path: `${ROUTES.OCTOPUSSYNC}#pricing`,
          id: 'pricing'
        },
        {
          label: 'Goals',
          path: `${ROUTES.OCTOPUSSYNC}#goals`,
          id: 'goals'
        },
        {
          label: 'FAQ',
          path: `${ROUTES.OCTOPUSSYNC}#faq`,
          id: 'faq'
        }
      ]
    },
    {
      label: 'Sentinel',
      path: ROUTES.SENTINEL,
      hasDropdown: true,
      submenu: [
        {
          label: 'Features',
          path: `${ROUTES.SENTINEL}#features`,
          id: 'features'
        },
        {
          label: 'Pricing',
          path: `${ROUTES.SENTINEL}#pricing`,
          id: 'pricing'
        },
        {
          label: 'How To',
          path: `${ROUTES.SENTINEL}#how-to`,
          id: 'how-to'
        }
      ]
    },
    {
      label: 'Login',
      path: ROUTES.LOGIN,
      hasDropdown: false
    },
    {
      label: 'Feature Request',
      path: '/feature-request',
      hasDropdown: false,
      isFeatureRequest: true
    },
    {
      label: 'Service',
      path: null,
      hasDropdown: false,
      isServiceStatus: true
    },
    {
      label: 'Contacts',
      path: ROUTES.CONTACTS,
      hasDropdown: false
    }
  ];
};

export const universalScrollTo = (position, duration = 800, behavior = 'smooth') => {
  window.scrollTo({
    top: position,
    behavior: behavior
  });
};
